import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { supportTicketsService } from '@/services/SupportTicketsService'
import { companiesService } from '@/services/CompaniesService'

export const useSupportTicketsStore = defineStore('supportTickets', () => {
  // State
  const tickets = ref([])
  const current = ref(null)
  const isLoading = ref(false)
  const isLoadingDetail = ref(false)
  const isSubmitting = ref(false)
  const error = ref(null)
  const pagination = ref({ page: 1, totalPages: 1, hasNextPage: false, total: 0 })

  // Getters
  const openTickets = computed(() =>
    tickets.value.filter((t) => ['open', 'in_progress', 'waiting_customer'].includes(t.status))
  )
  const resolvedTickets = computed(() =>
    tickets.value.filter((t) => ['resolved', 'closed'].includes(t.status))
  )

  // Actions
  async function fetchTickets({ page = 1, limit = 20 } = {}) {
    if (!companiesService.hasActiveCompany()) {
      tickets.value = []
      return
    }
    isLoading.value = true
    error.value = null
    try {
      const response = await supportTicketsService.getAll({ page, limit })
      tickets.value = Array.isArray(response?.data) ? response.data : []
      pagination.value = {
        page: response?.page || 1,
        totalPages: response?.totalPages || 1,
        hasNextPage: !!response?.hasNextPage,
        total: response?.total || 0
      }
    } catch (err) {
      error.value = err.message || 'No se pudieron cargar tus tickets de soporte'
    } finally {
      isLoading.value = false
    }
  }

  async function fetchTicket(id) {
    isLoadingDetail.value = true
    error.value = null
    current.value = null
    try {
      const ticket = await supportTicketsService.getById(id)
      current.value = ticket
      return ticket
    } catch (err) {
      error.value = err.message || 'No se pudo cargar el ticket'
      throw err
    } finally {
      isLoadingDetail.value = false
    }
  }

  async function createTicket(payload) {
    isSubmitting.value = true
    try {
      const { data } = await supportTicketsService.create(payload)
      tickets.value.unshift(data)
      return data
    } finally {
      isSubmitting.value = false
    }
  }

  async function replyTicket(id, body, attachments = []) {
    isSubmitting.value = true
    try {
      const { data: message } = await supportTicketsService.addMessage(id, body, attachments)
      if (current.value && String(current.value.id) === String(id)) {
        current.value.messages = [...(current.value.messages || []), message]
        current.value.last_message_at = message?.created_at || new Date().toISOString()
      }
      return message
    } finally {
      isSubmitting.value = false
    }
  }

  async function closeOrReopen(id, action) {
    isSubmitting.value = true
    try {
      const updated = await supportTicketsService.closeOrReopen(id, action)
      if (current.value && String(current.value.id) === String(id)) {
        current.value = { ...current.value, ...updated }
      }
      const index = tickets.value.findIndex((t) => String(t.id) === String(id))
      if (index !== -1) {
        tickets.value[index] = { ...tickets.value[index], ...updated }
      }
      return updated
    } finally {
      isSubmitting.value = false
    }
  }

  function $reset() {
    tickets.value = []
    current.value = null
    isLoading.value = false
    isLoadingDetail.value = false
    isSubmitting.value = false
    error.value = null
    pagination.value = { page: 1, totalPages: 1, hasNextPage: false, total: 0 }
  }

  return {
    tickets,
    current,
    isLoading,
    isLoadingDetail,
    isSubmitting,
    error,
    pagination,
    openTickets,
    resolvedTickets,
    fetchTickets,
    fetchTicket,
    createTicket,
    replyTicket,
    closeOrReopen,
    $reset
  }
})
